/**
 * "Elegant" — designer Cover 1.
 * Top + bottom soft scrims, letter-spaced kicker, huge serif headline,
 * spaced-caps subtitle, big serif stat numbers with hairline dividers,
 * "СВАЙП →" cue bottom-right. All chrome is translucent — the user's
 * photo (or chosen flat color) shows through.
 *
 * Layout reflows: cleared texts are skipped (headline rises when the
 * kicker is removed), and the swipe cue yields when long stats need
 * its space.
 */

import { SLIDE_W, SLIDE_H, type CarouselTemplate, type CoverSlideData, type SlideStyle } from "../types";
import {
  drawDesignerBackdrop, drawMockScrim, drawWatermark,
  drawTrackedText, measureTracked, drawStatRow,
  drawLongArrow, drawSerifHeadline,
} from "../shared";
import { NUMBER_FONT } from "../fonts";
import { fieldDiary } from "./field-diary";

function drawCover(ctx: CanvasRenderingContext2D, data: CoverSlideData, style: SlideStyle): void {
  const W = SLIDE_W, H = SLIDE_H, PAD = 64;
  const DISPLAY = style.fonts.display;
  const BODY = style.fonts.body;

  ctx.clearRect(0, 0, W, H);
  const textOnLight = drawDesignerBackdrop(ctx, data);
  drawMockScrim(ctx, 550, true, !textOnLight);
  drawMockScrim(ctx, 490, false, !textOnLight);

  const rgb = textOnLight ? "28,36,32" : "255,255,255";
  const solid = textOnLight ? "#1c2420" : "#ffffff";
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";

  // ── Kicker (optional — headline rises into its place when cleared) ──
  const kicker = data.kickerText.trim();
  const kickerSize = 34 * (data.kickerScale ?? 1);
  if (kicker) {
    ctx.font = `500 ${kickerSize}px ${BODY}`;
    ctx.fillStyle = `rgba(${rgb},0.58)`;
    drawTrackedText(ctx, kicker.toUpperCase(), PAD, 63 + kickerSize, 10 * (data.kickerScale ?? 1));
  }

  // ── Headline (design size 295 at the default 140 slider position) ──
  const size = 295 * (data.headlineFontSize / 140);
  const capTop = kicker ? 63 + kickerSize + 65 : 96;
  ctx.fillStyle = solid;
  const lastBaseline = drawSerifHeadline(ctx, data.headlineTitle, PAD, 0, W - PAD * 2, size, DISPLAY, { capTop });

  // ── Subtitle (optional) ──
  const subtitle = data.subtitle.trim();
  if (subtitle) {
    const s = data.subtitleScale ?? 1;
    ctx.font = `300 ${46 * s}px ${BODY}`;
    ctx.fillStyle = `rgba(${rgb},0.78)`;
    drawTrackedText(ctx, subtitle.toUpperCase(), PAD, lastBaseline + 68 * Math.max(s, 0.8), 12 * s);
  }

  // ── Bottom-left stats: big serif number over spaced-caps label ──
  const st = data.statsScale ?? 1;
  const statsEnd = drawStatRow(ctx, data.stats, {
    x0: PAD,
    numBase: 1226,
    labelBase: 1226 + 55 * st,
    numFont: `400 ${84 * st}px ${NUMBER_FONT}`,
    textFont: `400 ${46 * st}px ${DISPLAY}`,
    labelFont: `500 ${26 * st}px ${BODY}`,
    labelTracking: 6 * st,
    labelAlpha: 0.46,
    solid,
    rgb,
    divider: { top: 1162, height: 72, alpha: 0.22 },
    segGap: 50,
  });

  // ── Swipe cue, bottom-right — yields to long stats ──
  const cue = (data.swipeText?.trim() || "СВАЙП").toUpperCase();
  ctx.font = `500 26px ${BODY}`;
  const cueStart = 901 - measureTracked(ctx, cue, 8);
  if (statsEnd < cueStart - 40) {
    ctx.fillStyle = `rgba(${rgb},0.42)`;
    drawTrackedText(ctx, cue, cueStart, 1243, 8);
    ctx.strokeStyle = solid;
    drawLongArrow(ctx, 1003, 1235, 66, 7, 20);
  }

  drawWatermark(ctx, style, textOnLight);
}

export const elegant: CarouselTemplate = {
  id: "elegant",
  name: "Elegant",
  premium: false,
  drawCover,
  drawSpecies: fieldDiary.drawSpecies,
  drawClosing: fieldDiary.drawClosing,
};
